import { get, keyBy, map } from "lodash";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { getColorFromId } from "../../components/labels/utils";
import { ApiResponse, fetchApi } from "../../utils/api";
import { Entity } from "../../utils/constants";
import { Label } from "../../utils/types";
import usePrevious from "../../utils/usePrevious";
import { useLoading } from "../loading";
import { LoadingKey } from "../loading/LoadingProvider";
import { useWorkspaces } from "../workspaces";

interface Props {}

export type ContextValue = {
  labels: LabelsMap;
  linkedLabels: LinkedLabelsMap;
  fetchLabels: () => Promise<void>;
  addLabel: (label: LabelValues) => Promise<ApiResponse<Label>>;
  updateLabel: (uuid: string, data: LabelValues) => Promise<ApiResponse<Label>>;
  removeLabel: (uuid: string) => Promise<ApiResponse<[]>>;
  addItemLabel: (
    entity: Entity,
    itemUuid: string,
    name: string
  ) => Promise<ApiResponse<Label[]>>;
  addBulkItemLabels: (
    entity: Entity,
    itemUuids: string[],
    names: string[]
  ) => Promise<ApiResponse<Label[][]>>;
  updateItemLabels: (
    entity: Entity,
    itemUuid: string,
    labelUuids: string[]
  ) => Promise<ApiResponse<Label[]>>;
};

export const LabelsContext = React.createContext<ContextValue | undefined>(
  undefined
);

export const LabelsProvider: React.FC<Props> = (props) => {
  const { addLoader, removeLoader } = useLoading();
  const { currentWorkspaceId } = useWorkspaces();
  const [labels, setLabels] = useState<LabelsMap>({});
  const [linkedLabels, setLinkedLabels] = useState<LinkedLabelsMap>({});

  const fetchLabels = useCallback(async () => {
    if (!currentWorkspaceId) return;

    addLoader(LoadingKey.workspaceLabels);

    const response = await fetchApi(
      `/api/workspace/${currentWorkspaceId}/labels`
    );

    const apiResponse: ApiResponse<ListLabelsData> = await response.json();

    if (apiResponse.status) {
      setLabels(apiResponse.data.labels);
      setLinkedLabels(apiResponse.data.linkedLabels);
    }

    removeLoader(LoadingKey.workspaceLabels);
  }, [addLoader, currentWorkspaceId, removeLoader]);

  const prevWorkspaceId = usePrevious(currentWorkspaceId);
  useEffect(() => {
    if (
      currentWorkspaceId &&
      prevWorkspaceId &&
      currentWorkspaceId !== prevWorkspaceId
    ) {
      setLabels({});
      setLinkedLabels({});
    }
  }, [currentWorkspaceId, prevWorkspaceId]);

  useEffect(() => {
    fetchLabels();
  }, [fetchLabels]);

  const addLabel = useCallback(
    async ({ name, color, type }: LabelValues) => {
      const response = await fetchApi(
        `/api/workspace/${currentWorkspaceId}/label`,
        {
          method: "POST",
          body: JSON.stringify({ name, color, type })
        }
      );

      const apiResponse: ApiResponse<Label> = await response.json();

      if (apiResponse.status) {
        await fetchLabels();
      }

      return apiResponse;
    },
    [currentWorkspaceId, fetchLabels]
  );

  const updateLabel = useCallback(
    async (uuid: string, { name, color, type }: LabelValues) => {
      const response = await fetchApi(
        `/api/workspace/${currentWorkspaceId}/label/${uuid}`,
        {
          method: "PUT",
          body: JSON.stringify({ name, color, type })
        }
      );

      const apiResponse: ApiResponse<Label> = await response.json();

      if (apiResponse.status) {
        await fetchLabels();
      }

      return apiResponse;
    },
    [currentWorkspaceId, fetchLabels]
  );

  const removeLabel = useCallback(
    async (uuid: string) => {
      const response = await fetchApi(
        `/api/workspace/${currentWorkspaceId}/label/${uuid}`,
        {
          method: "DELETE"
        }
      );

      const apiResponse: ApiResponse<[]> = await response.json();

      if (apiResponse.status) {
        await fetchLabels();
      }

      return apiResponse;
    },
    [currentWorkspaceId, fetchLabels]
  );

  const addItemLabel = useCallback(
    async (entity: Entity, itemUuid: string, name: string) => {
      const response = await fetchApi(
        `/api/workspace/${currentWorkspaceId}/${entity}/${itemUuid}/labels`,
        {
          method: "POST",
          body: JSON.stringify({
            labels: [
              {
                name,
                color: getColorFromId(name),
                type: entity
              }
            ]
          })
        }
      );

      const apiResponse: ApiResponse<Label[]> = await response.json();

      if (apiResponse.status) {
        setLinkedLabels((prevState) => ({
          ...prevState,
          [itemUuid]: map(apiResponse.data, "uuid")
        }));
      }

      return apiResponse;
    },
    [currentWorkspaceId]
  );

  const addBulkItemLabels = useCallback(
    async (entity: Entity, itemUuids: string[], names: string[]) => {
      const response = await fetchApi(
        `/api/workspace/${currentWorkspaceId}/labels`,
        {
          method: "POST",
          body: JSON.stringify({
            type: entity,
            type_uuids: itemUuids,
            labels: names.map((name) => ({
              name,
              color: getColorFromId(name),
              type: entity
            }))
          })
        }
      );

      const apiResponse: ApiResponse<Label[][]> = await response.json();

      if (apiResponse.status) {
        setLabels((prevState) => ({
          ...prevState,
          ...keyBy(
            apiResponse.data[0].map((label) =>
              label.title ? { ...label, name: label.title } : label
            ),
            "uuid"
          )
        }));

        setLinkedLabels((prevState) => {
          itemUuids.forEach((uuid) => {
            prevState[uuid] = map(apiResponse.data[0], "uuid");
          });

          return prevState;
        });
      }

      return apiResponse;
    },
    [currentWorkspaceId]
  );

  const updateItemLabels = useCallback(
    async (entity: Entity, itemUuid: string, labelUuids: string[]) => {
      const response = await fetchApi(
        `/api/workspace/${currentWorkspaceId}/${entity}/${itemUuid}/labels`,
        {
          method: "PUT",
          body: JSON.stringify({
            labels: labelUuids.map((labelUuid) => ({
              name: get(labels, [labelUuid, "name"])
            }))
          })
        }
      );

      const apiResponse: ApiResponse<Label[]> = await response.json();

      if (apiResponse.status) {
        setLinkedLabels((prevState) => {
          if (!prevState[itemUuid]) prevState[itemUuid] = [];

          return {
            ...prevState,
            [itemUuid]: labelUuids
          };
        });
      }

      return apiResponse;
    },
    [currentWorkspaceId, labels]
  );

  const value = useMemo(
    () => ({
      labels,
      fetchLabels,
      addLabel,
      updateLabel,
      removeLabel,
      linkedLabels,
      addItemLabel,
      addBulkItemLabels,
      updateItemLabels
    }),
    [
      labels,
      fetchLabels,
      addLabel,
      updateLabel,
      removeLabel,
      linkedLabels,
      addItemLabel,
      addBulkItemLabels,
      updateItemLabels
    ]
  );

  return <LabelsContext.Provider value={value} {...props} />;
};

export const useLabels = (): ContextValue => {
  const context = useContext(LabelsContext);

  if (context === undefined) {
    throw new Error("useLabels must be used within an LabelsProvider");
  }

  return context;
};

//
// Utils
//

export interface LabelValues {
  name: string;
  color: string;
  type: string;
}

export interface LabelsMap {
  [labelUuid: string]: Label;
}

interface LinkedLabelsMap {
  [linkedUuid: string]: string[]; // array of linked labelUuids for each item based on its uuid
}

interface ListLabelsData {
  labels: LabelsMap;
  linkedLabels: LinkedLabelsMap;
}
